<template>
  <div class="homepage">
    <!-- Hero Slider -->
    <hero-banner :sliders="sliders" :hero-banner="heroBanner" :categories="formattedCategories" />

    <!-- Debug Info (Remove in production) -->
    <div v-if="sections.length === 0" style="padding: 20px; text-align: center; color: #666;">
      <p>No collections found. Please add collections with products to the database.</p>
      <p style="font-size: 12px;">Check that collections have: is_active=1, show_on_homepage=1, and product_ids array.</p>
    </div>

    <!-- Dynamic Collections Sections -->
    <template v-for="(section, index) in activeSections" :key="section.id">
      <product-section
        :title="section.name"
        :products="section.products"
        :view-all-link="section.view_all_link"
        :max-items="8"
        :bg-gradient="section.bg_gradient"
        :banner="section.banner"
        :banner-image="section.image"
        :banner-description="section.description"
        :has-banner="!!(section.banner || section.image)"
        :section-type="section.type"
        compact
      />

      <!-- Categories Grid after first section -->
      <categories-grid v-if="index === 0" :categories="formattedCategories" :limit="12" />

      <!-- Home Banner based on position (position 1 = after 1st section, etc) -->
      <home-banner
        v-if="shouldShowBanner(index + 1)"
        :banners="getBannersForPosition(index + 1)"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import HeroBanner from '../components/home/HeroBanner.vue'
import ProductSection from '../components/home/ProductSection.vue'
import HomeBanner from '../components/home/HomeBanner.vue'
import CategoriesGrid from '../components/home/CategoriesGrid.vue'

const props = defineProps({
  sections: {
    type: Array,
    default: () => []
  },
  categories: {
    type: Array,
    default: () => []
  },
  sliders: {
    type: Array,
    default: () => []
  },
  heroBanner: {
    type: Object,
    default: null
  },
  banners: {
    type: Array,
    default: () => []
  }
})

// Format categories for menu navigation
const formattedCategories = computed(() => {
  // Categories are already filtered and sorted by the backend
  return (props.categories || []).map(cat => ({
    name: cat.name,
    slug: cat.slug,
    icon: cat.icon || 'fas fa-folder',
    image: cat.image || cat.banner || null,
    banner: cat.banner || null,
    link: `/${cat.slug}`,
    subcategories: (cat.subCategories || cat.sub_categories || []).map(sub => ({
      name: sub.name,
      slug: sub.slug,
      link: `/${cat.slug}/${sub.slug}`,
      subsubcategories: (sub.subSubCategories || sub.sub_sub_categories || []).map(subsub => ({
        name: subsub.name,
        slug: subsub.slug,
        link: `/${cat.slug}/${sub.slug}/${subsub.slug}`
      }))
    }))
  }))
})

// Make categories available to legacy components (if needed)
onMounted(() => {
  window.categories = formattedCategories.value
})

// Filter only active sections with products
const activeSections = computed(() => {
  const filtered = (props.sections || []).filter(section => section.active && section.products?.length > 0)
  return filtered
})

// Get banners for a specific position (position 1 = after 1st section)
const getBannersForPosition = (position) => {
  return (props.banners || []).filter(banner => banner.position === position)
}

// Default positions to show banners (even with fallback if no database banners)
const defaultBannerPositions = [1]

// Determine if banner should show at this position
const shouldShowBanner = (position) => {
  const hasBannersForPosition = getBannersForPosition(position).length > 0
  const isDefaultPosition = defaultBannerPositions.includes(position)
  return hasBannersForPosition || isDefaultPosition
}
</script>
